import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Divider,
  Menu,
  MenuItem,
  Typography,
  Stack,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../../constants/routes';
import Grid from '@mui/material/Grid2';
import BarChartIcon from '@mui/icons-material/BarChart';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import InsightsIcon from '@mui/icons-material/Insights';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PercentIcon from '@mui/icons-material/Percent';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import SouthEastIcon from '@mui/icons-material/SouthEast';
import TagIcon from '@mui/icons-material/Tag';
import { Card, IconButton } from '../../../../components/ui';
import { useFeatureTheme } from '../../../../theme/hooks/useFeatureTheme';
import AnalyticsChartRenderer from '../charts/AnalyticsChartRenderer';
import { ANALYTICS_COPY } from '../../constants/analyticsCopy';
import { reorderIds } from '../../utils/reorderIds';
import {
  PIN_COL_MAX,
  PIN_ROW_HEIGHT,
  chartHeightForRowSpan,
  nextPinSize,
  pinGridSize,
  readPinLayout,
} from '../../utils/pinLayout';
import ReportCardActions from './ReportCardActions';
import ReportCardFilters from './ReportCardFilters';
import { querySpecToChartType } from '../../utils/chartTypeMapping';
import { resolveChartAxisLabels } from '../../utils/chartAxisLabels';
import { CHART_TYPES } from '../../constants/chartTypes';
import { sortChartResultByValue } from '../../utils/chartFormatUtils';
import WhatsappTemplateInsightsView from '../insights/WhatsappTemplateInsightsView';
import { normalizeWhatsappTemplateOptions } from '../../utils/templateOptions';

export default function PinnedReportGrid({
  items = [],
  sx,
  arranging = false,
  canManage = false,
  canDelete = false,
  onEditReport,
  onDeleteReport,
  onReorder,
  onResize,
  onApplyFilters,
  chartTypes = {},
  onChangeChartType,
  sortOrder = '',
  canExport = false,
  orgId,
  onExport,
}) {
  const { semantic, typography, shadows, radius } = useFeatureTheme();
  const theme = useTheme();
  const navigate = useNavigate();
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));
  const maxColumns = isLg || isMd ? 3 : isSm ? 2 : 1;
  const gridRef = useRef(null);
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [chartMenu, setChartMenu] = useState(null);
  const [reportDisplayModes, setReportDisplayModes] = useState({});
  const [inlineInsightsReportId, setInlineInsightsReportId] = useState(null);

  const availableTemplateOptions = useMemo(() => {
    const allTemplates = [];
    items.forEach((it) => {
      const opts = normalizeWhatsappTemplateOptions(it?.preview, '');
      opts.forEach((o) => {
        if (o.id && !allTemplates.some((existing) => existing.id === o.id)) {
          allTemplates.push(o);
        }
      });
      const ids = [
        ...(Array.isArray(it?.cardFilters?.templateIds) ? it.cardFilters.templateIds : (it?.cardFilters?.templateId ? [it.cardFilters.templateId] : [])),
        ...(Array.isArray(it?.querySpec?.template_id) ? it.querySpec.template_id : (it?.querySpec?.template_id ? [it.querySpec.template_id] : [])),
      ].filter((id) => Boolean(id) && id !== 'all');

      ids.forEach((tId) => {
        if (!allTemplates.some((existing) => existing.id === String(tId))) {
          allTemplates.push({ id: String(tId), label: it?.report?.name || String(tId) });
        }
      });
    });
    return allTemplates;
  }, [items]);

  if (!items.length) {
    return (
      <Typography
        sx={{
          fontSize: typography.fontSizeMd,
          color: semantic.text.secondary,
        }}
      >
        {ANALYTICS_COPY.noPinnedReports}
      </Typography>
    );
  }

  const finishDrag = () => {
    setDragId(null);
    setOverId(null);
  };

  const dropOn = (targetId) => {
    if (!arranging || !dragId || dragId === targetId) {
      finishDrag();
      return;
    }
    const ids = items.map((item) => item.report?.report_id).filter(Boolean);
    onReorder?.(reorderIds(ids, dragId, targetId));
    finishDrag();
  };

  const startResize = (event, reportId, layout) => {
    event.preventDefault();
    event.stopPropagation();
    const handle = event.currentTarget;
    const container = gridRef.current;
    if (!container) return;
    const gapValue = theme.spacing(2);
    const gap = typeof gapValue === 'number' ? gapValue : parseFloat(gapValue) || 16;
    const columnWidth = (container.clientWidth - gap * (maxColumns - 1)) / maxColumns;
    const startX = event.clientX;
    const startY = event.clientY;
    const origin = { ...layout };
    let latest = origin;

    const move = (moveEvent) => {
      latest = nextPinSize({
        colSpan: origin.colSpan,
        rowSpan: origin.rowSpan,
        deltaX: moveEvent.clientX - startX,
        deltaY: moveEvent.clientY - startY,
        columnWidth,
        rowHeight: PIN_ROW_HEIGHT,
        maxColumns: PIN_COL_MAX,
      });
      setDraft({ reportId, ...latest });
    };

    const end = () => {
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', end);
      handle.removeEventListener('pointercancel', end);
      setDraft(null);
      if (latest.colSpan !== origin.colSpan || latest.rowSpan !== origin.rowSpan) {
        onResize?.(reportId, latest);
      }
    };

    handle.setPointerCapture(event.pointerId);
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', end);
    handle.addEventListener('pointercancel', end);
  };

  return (
    <Box ref={gridRef}>
    <Grid container spacing={2}>
      {items.map((item) => {
        const report = item.report || {};
        const preview = item.preview;
        const reportId = report.report_id;
        const querySpec = item.querySpec || report.query_spec || {};
        const savedChartType = querySpecToChartType(querySpec);
        const selectedChartType = chartTypes[reportId] || savedChartType;
      const isTableReport = selectedChartType === 'table' || preview?.result?.kind === 'table';
        const savedLayout = readPinLayout(item.pin, isTableReport ? 3 : 1);
        const layout = draft?.reportId === reportId
          ? { colSpan: draft.colSpan, rowSpan: draft.rowSpan }
          : savedLayout;
        const chartHeight = chartHeightForRowSpan(layout.rowSpan);
        const displayResult = sortChartResultByValue(preview?.result, sortOrder);
        const metricId = querySpec.metric || 'count';
        const firstSeries = item.preview?.result?.series?.[0];
        const metricLabel = firstSeries?.label || metricId;
        const chartLabels = resolveChartAxisLabels({
          chartType: selectedChartType,
          dimension: querySpec.dimension,
          metric: metricId,
          aggregation: querySpec.metric_aggregation || 'count',
          measureField: querySpec.metric_field,
          metrics: [{ id: metricId, label: metricLabel }],
          columns: querySpec.columns || [],
        });
        const isInterveneOverview = Array.isArray(preview?.result?.metrics)
          && preview.result.metrics.some((metric) => metric.id === 'avg_agent_response');
        const isOver = arranging && overId === reportId && dragId !== reportId;
        const isResizing = draft?.reportId === reportId;
        return (
          <React.Fragment key={reportId}>
            <Grid size={pinGridSize(layout.colSpan)} sx={{ display: 'flex' }}>
              <Card
              data-report-card
              variant="elevated"
              onDragOver={arranging ? (event) => {
                event.preventDefault();
                if (overId !== reportId) setOverId(reportId);
              } : undefined}
              onDrop={arranging ? (event) => {
                event.preventDefault();
                dropOn(reportId);
              } : undefined}
              sx={{
                position: 'relative',
                width: '100%',
                p: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                opacity: dragId === reportId ? 0.55 : 1,
                boxShadow: isOver || isResizing ? shadows.lg : undefined,
                outline: isOver || isResizing ? `2px solid ${semantic.interactive.primary}` : 'none',
                outlineOffset: isOver || isResizing ? -2 : 0,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
                draggable={arranging}
                onDragStart={arranging ? (event) => {
                  if (event.target.closest('button')) {
                    event.preventDefault();
                    return;
                  }
                  event.dataTransfer.effectAllowed = 'move';
                  event.dataTransfer.setData('text/plain', reportId);
                  setDragId(reportId);
                } : undefined}
                onDragEnd={finishDrag}
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: '1px solid',
                  borderColor: semantic.borders.border,
                  cursor: arranging ? 'grab' : 'default',
                  '&:active': arranging ? { cursor: 'grabbing' } : undefined,
                }}
              >
                <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                  {arranging ? (
                    <DragIndicatorIcon
                      aria-hidden
                      sx={{ color: semantic.text.muted, fontSize: typography.fontSizeXl, flexShrink: 0 }}
                    />
                  ) : null}
                <Box
                  sx={{ minWidth: 0, cursor: 'pointer' }}
                  onClick={() => navigate(ROUTES.FEATURES.ANALYTICS_REPORT.replace(':reportId', reportId), {
                    state: {
                      report: item.report,
                      preview: item.preview,
                      querySpec: item.querySpec,
                      cardFilters: item.cardFilters,
                      orgId,
                    },
                  })}
                >
                  <Typography
                    sx={{
                      minWidth: 0,
                      fontSize: typography.fontSizeLg,
                      fontWeight: typography.fontWeightSemibold,
                      lineHeight: typography.lineHeightHeading,
                      color: semantic.text.primary,
                      '&:hover': {
                        color: semantic.interactive.primary,
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {report.name}
                  </Typography>
                  {item.rangeLabel ? (
                    <Typography
                      sx={{
                        mt: 0.25,
                        fontSize: typography.fontSizeSm,
                        color: semantic.text.secondary,
                      }}
                    >
                      {item.rangeLabel}
                    </Typography>
                  ) : null}
                </Box>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {canExport && item.querySpec ? (
                    <Tooltip title={ANALYTICS_COPY.exportCsv}>
                      <IconButton
                        size="sm"
                        variant="ghost"
                        aria-label={ANALYTICS_COPY.exportCsv}
                        onClick={(event) => {
                          event.stopPropagation();
                          onExport?.(item.querySpec, item.preview?.result, item.report?.name);
                        }}
                      >
                        <FileDownloadOutlinedIcon sx={{ fontSize: typography.fontSizeLg }} />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                  {item.querySpec?.dataset === 'whatsapp_templates' ? (
                    <Tooltip title={inlineInsightsReportId === reportId ? 'Hide Insights' : 'Show Insights on Dashboard (Below)'}>
                      <IconButton
                        size="sm"
                        variant="ghost"
                        aria-label={`Toggle Insights for ${report.name}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          setInlineInsightsReportId((prev) => (prev === reportId ? null : reportId));
                        }}
                      >
                        <InsightsIcon sx={{ fontSize: typography.fontSizeLg, color: inlineInsightsReportId === reportId ? '#059669' : '#00a884' }} />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                  <Tooltip title="View full report">
                    <IconButton
                      size="sm"
                      variant="ghost"
                      aria-label={`View ${report.name}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        navigate(ROUTES.FEATURES.ANALYTICS_REPORT.replace(':reportId', reportId), {
                          state: {
                            report: item.report,
                            preview: item.preview,
                            querySpec: item.querySpec,
                            cardFilters: item.cardFilters,
                            orgId,
                          },
                        });
                      }}
                    >
                      <OpenInNewIcon sx={{ fontSize: typography.fontSizeLg }} />
                    </IconButton>
                  </Tooltip>
                  <ReportCardFilters
                    dataset={item.querySpec?.dataset}
                    orgId={orgId}
                    appliedFilters={item.cardFilters}
                    templates={availableTemplateOptions}
                    querySpec={item.querySpec}
                    campaignIds={item.querySpec?.campaign_ids || []}
                    leadsFormIds={item.querySpec?.leads_form_ids || []}
                    onApply={(filters) => onApplyFilters?.(reportId, filters)}
                  />
                  {['line', 'bar'].includes(savedChartType) ? (
                    <>
                      <Tooltip title="Change chart type">
                        <IconButton
                          size="sm"
                          variant="ghost"
                          aria-label={`Change chart type for ${report.name}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            setChartMenu({ anchorEl: event.currentTarget, reportId });
                          }}
                        >
                          <MoreVertIcon sx={{ fontSize: typography.fontSizeLg }} />
                        </IconButton>
                      </Tooltip>
                      <Menu
                        anchorEl={chartMenu?.reportId === reportId ? chartMenu.anchorEl : null}
                        open={chartMenu?.reportId === reportId}
                        onClose={() => setChartMenu(null)}
                        onClick={(event) => event.stopPropagation()}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        MenuListProps={{ sx: { py: 0.5, minWidth: 160 } }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ px: 2, py: 0.5, color: 'text.secondary', display: 'block', fontWeight: 700, letterSpacing: 0.5 }}
                        >
                          CHART TYPE
                        </Typography>
                        <MenuItem
                          selected={selectedChartType === 'bar'}
                          onClick={() => {
                            onChangeChartType?.(reportId, 'bar');
                            setChartMenu(null);
                          }}
                        >
                          <BarChartIcon sx={{ fontSize: 18, mr: 1, color: '#1976d2' }} />
                          Bar Chart
                        </MenuItem>
                        <MenuItem
                          selected={selectedChartType === 'line'}
                          onClick={() => {
                            onChangeChartType?.(reportId, 'line');
                            setChartMenu(null);
                          }}
                        >
                          <ShowChartIcon sx={{ fontSize: 18, mr: 1, color: '#1976d2' }} />
                          Line Chart
                        </MenuItem>

                        <Divider sx={{ my: 0.5 }} />

                        <Typography
                          variant="caption"
                          sx={{ px: 2, py: 0.5, color: 'text.secondary', display: 'block', fontWeight: 700, letterSpacing: 0.5 }}
                        >
                          DISPLAY MODE
                        </Typography>
                        <MenuItem
                          selected={(reportDisplayModes[reportId] || 'count') === 'count'}
                          onClick={() => {
                            setReportDisplayModes((prev) => ({ ...prev, [reportId]: 'count' }));
                            setChartMenu(null);
                          }}
                        >
                          <TagIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
                          Count (#)
                        </MenuItem>
                        <MenuItem
                          selected={(reportDisplayModes[reportId] || 'count') === 'percentage'}
                          onClick={() => {
                            setReportDisplayModes((prev) => ({ ...prev, [reportId]: 'percentage' }));
                            setChartMenu(null);
                          }}
                        >
                          <PercentIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
                          Percentage (%)
                        </MenuItem>

                        {item.querySpec?.dataset === 'whatsapp_templates' ? (
                          <>
                            <Divider sx={{ my: 0.5 }} />
                            <Typography
                              variant="caption"
                              sx={{ px: 2, py: 0.5, color: 'text.secondary', display: 'block', fontWeight: 700, letterSpacing: 0.5 }}
                            >
                              TEMPLATE INSIGHTS
                            </Typography>
                            {/* New route commented out per user request
                            <MenuItem
                              onClick={() => {
                                const tid = item.cardFilters?.template_id || (Array.isArray(item.querySpec?.template_id) ? item.querySpec.template_id[0] : item.querySpec?.template_id) || '';
                                const sdate = item.querySpec?.date_range?.start || '';
                                setChartMenu(null);
                                navigate(`/r/v2/analytics/template-insights/${tid}${sdate ? `?sent_date=${sdate}` : ''}`);
                              }}
                            >
                              <OpenInNewIcon sx={{ fontSize: 18, mr: 1, color: '#1976d2' }} />
                              Open Full Insights Page (New Route)
                            </MenuItem>
                            */}
                            <MenuItem
                              onClick={() => {
                                setInlineInsightsReportId((prev) => (prev === reportId ? null : reportId));
                                setChartMenu(null);
                              }}
                            >
                              <InsightsIcon sx={{ fontSize: 18, mr: 1, color: '#00a884' }} />
                              {inlineInsightsReportId === reportId ? 'Hide Insights' : 'Show Insights on Dashboard (Below)'}
                            </MenuItem>
                          </>
                        ) : null}
                      </Menu>
                    </>
                  ) : null}
                  <ReportCardActions
                    reportId={reportId}
                    canManage={arranging && canManage}
                    canDelete={arranging && canDelete}
                    onEdit={onEditReport}
                    onDelete={onDeleteReport}
                  />
                </Stack>
              </Stack>
              <Box
                sx={{
                  flex: 1,
                  minHeight: isTableReport ? Math.max(chartHeight, 320) : chartHeight,
                  maxHeight: isInterveneOverview ? chartHeight : undefined,
                  px: 1.5,
                  py: 1.5,
                  bgcolor: semantic.surfaces.surfaceSubtle,
                  display: 'flex',
                  alignItems: isTableReport || isInterveneOverview ? 'flex-start' : 'center',
                  overflow: isInterveneOverview ? 'auto' : 'hidden',
                }}
              >
                <Box sx={{ width: '100%', minWidth: 0 }}>
                  <AnalyticsChartRenderer
                    result={displayResult}
                    visualization={selectedChartType}
                    chartLabels={chartLabels}
                    sx={sx}
                    compact
                    height={chartHeight}
                    querySpec={item.querySpec}
                    orgId={orgId}
                    displayMode={reportDisplayModes[reportId] || 'count'}
                  />
                </Box>
              </Box>
              {arranging ? (
                <Box
                  component="button"
                  type="button"
                  aria-label={ANALYTICS_COPY.resizeReport}
                  onPointerDown={(event) => startResize(event, reportId, savedLayout)}
                  sx={{
                    position: 'absolute',
                    right: 10,
                    bottom: 10,
                    width: 28,
                    height: 28,
                    p: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: `${radius.sm}px`,
                    border: `1px solid ${semantic.borders.border}`,
                    bgcolor: semantic.surfaces.surface,
                    color: semantic.text.secondary,
                    cursor: 'nwse-resize',
                    boxShadow: shadows.sm,
                    touchAction: 'none',
                    '&:hover': {
                      color: semantic.interactive.primary,
                      borderColor: semantic.interactive.primary,
                    },
                    '&:focus-visible': {
                      outline: `2px solid ${semantic.interactive.primary}`,
                      outlineOffset: 2,
                    },
                  }}
                >
                  <SouthEastIcon sx={{ fontSize: typography.fontSizeLg }} />
                </Box>
              ) : null}
            </Card>
          </Grid>
          {inlineInsightsReportId === reportId && (
            <Grid key={`${reportId}-inline-insights`} size={12} sx={{ width: '100%', mt: 0.5, mb: 1.5 }}>
              <WhatsappTemplateInsightsView
                templateId={
                  item.cardFilters?.template_id
                  || (Array.isArray(item.querySpec?.template_id)
                    ? item.querySpec.template_id[0]
                    : item.querySpec?.template_id)
                  || ''
                }
                templateName={report.name}
                companyId={orgId}
                sentDate={item.querySpec?.date_range?.start || ''}
                availableTemplates={availableTemplateOptions}
                onClose={() => setInlineInsightsReportId(null)}
                /* onNavigateToRoute={() => {
                  const tid = item.cardFilters?.template_id || (Array.isArray(item.querySpec?.template_id) ? item.querySpec.template_id[0] : item.querySpec?.template_id) || '';
                  const sdate = item.querySpec?.date_range?.start || '';
                  navigate(`/r/v2/analytics/template-insights/${tid}${sdate ? `?sent_date=${sdate}` : ''}`);
                }} */
              />
            </Grid>
          )}
        </React.Fragment>
      );
    })}
  </Grid>
  </Box>
);
}
